import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWorkspace } from '../context/WorkspaceContext';
import type { EfficiencyMetric, ROIRecommendation } from '../lib/types';

const exampleDescriptions = [
  '강남에서 네일샵을 운영하고 있어요. 직원 3명과 함께 일하고 있고, 고객 관리와 예약 관리가 어려워요. 단골 고객을 늘리고 싶어요.',
  '프리랜서 웹 개발자입니다. 여러 클라이언트 프로젝트를 동시에 진행하고 있어서 일정 관리가 힘들어요. 효율적으로 업무를 관리하고 싶습니다.',
  '온라인으로 수제 악세서리를 판매하고 있어요. 매출을 늘리고 싶고, 고객 관리를 체계화하고 싶습니다.',
];

export function OnboardingPage() {
  const [description, setDescription] = useState('');
  const [step, setStep] = useState<'input' | 'analyzing' | 'complete'>('input');
  const { analyzeAndSetup, analysis, roiDiagnosis, isLoading } = useWorkspace();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!description.trim()) return;

    setStep('analyzing');
    try {
      await analyzeAndSetup(description);
      setStep('complete');
    } catch (error) {
      console.error('Setup failed:', error);
      setStep('input');
    }
  };

  const handleContinue = () => {
    navigate('/dashboard');
  };

  if (step === 'analyzing') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-accent-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-6"></div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            비즈니스를 분석하고 있습니다...
          </h2>
          <p className="text-gray-600">
            AI가 최적의 워크스페이스를 준비하고 있어요
          </p>
        </div>
      </div>
    );
  }

  if (step === 'complete' && analysis) {
    const typeNames: Record<string, string> = {
      retail: '소매업',
      service: '서비스업',
      consulting: '컨설팅',
      restaurant: '요식업',
      freelance: '프리랜서',
      agency: '에이전시',
      ecommerce: '이커머스',
      manufacturing: '제조업',
      other: '비즈니스',
    };

    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-accent-50 py-12">
        <div className="max-w-3xl mx-auto px-4">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">✅</span>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              워크스페이스가 준비되었습니다!
            </h1>
            <p className="text-gray-600">
              "{analysis.businessName}"을 위한 맞춤 워크스페이스를 만들었어요
            </p>
          </div>

          {/* ROI Diagnosis Hero Cards */}
          {roiDiagnosis && (
            <div className="mb-8">
              <h2 className="font-semibold text-lg mb-4 text-center">예상 ROI 진단</h2>

              {/* Main Value Proposition */}
              <div className="bg-gradient-to-r from-primary-600 to-accent-600 rounded-2xl p-6 text-white mb-4">
                <div className="grid grid-cols-2 gap-6">
                  <div className="text-center">
                    <div className="text-3xl font-bold">
                      ₩{roiDiagnosis.estimatedMonthlySavings.toLocaleString()}
                    </div>
                    <div className="text-sm opacity-90 mt-1">월 예상 비용 절감</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold">
                      {roiDiagnosis.estimatedTimeSavedHours}시간
                    </div>
                    <div className="text-sm opacity-90 mt-1">월 예상 시간 절약</div>
                  </div>
                </div>
              </div>

              {/* Efficiency Metrics Grid */}
              <div className="grid grid-cols-3 gap-3 mb-4">
                <EfficiencyCard metric={roiDiagnosis.timeEfficiency} icon="⏱️" />
                <EfficiencyCard metric={roiDiagnosis.costEfficiency} icon="💰" />
                <EfficiencyCard metric={roiDiagnosis.automationPotential} icon="⚡" />
              </div>

              {/* Top Recommendations */}
              <div className="card">
                <h3 className="font-semibold text-gray-900 mb-3">주요 개선 제안</h3>
                <div className="space-y-2">
                  {roiDiagnosis.recommendations.slice(0, 3).map((rec) => (
                    <RecommendationItem key={rec.id} recommendation={rec} />
                  ))}
                </div>
              </div>
            </div>
          )}

          <div className="card mb-6">
            <h2 className="font-semibold text-lg mb-4">분석 결과</h2>
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div>
                <div className="text-sm text-gray-500">사업 유형</div>
                <div className="font-medium">{typeNames[analysis.businessType] || analysis.businessType}</div>
              </div>
              <div>
                <div className="text-sm text-gray-500">산업 분야</div>
                <div className="font-medium">{analysis.industry}</div>
              </div>
              <div>
                <div className="text-sm text-gray-500">규모</div>
                <div className="font-medium">
                  {analysis.size === 'solo' ? '1인 사업' : analysis.size === 'small' ? '소규모' : '중규모'}
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-500">타겟 고객</div>
                <div className="font-medium">{analysis.targetCustomers.join(', ')}</div>
              </div>
            </div>

            <div className="mb-4">
              <div className="text-sm text-gray-500 mb-2">주요 목표</div>
              <div className="flex flex-wrap gap-2">
                {analysis.goals.map((goal, idx) => (
                  <span key={idx} className="badge-primary">{goal}</span>
                ))}
              </div>
            </div>

            <div>
              <div className="text-sm text-gray-500 mb-2">해결해야 할 과제</div>
              <div className="flex flex-wrap gap-2">
                {analysis.challenges.map((challenge, idx) => (
                  <span key={idx} className="badge-warning">{challenge}</span>
                ))}
              </div>
            </div>
          </div>

          <div className="card mb-6">
            <h2 className="font-semibold text-lg mb-4">추천 모듈</h2>
            <div className="space-y-3">
              {analysis.suggestedModules.map((module, idx) => (
                <div key={idx} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className={`w-2 h-2 rounded-full mt-2 ${
                    module.priority === 'essential' ? 'bg-green-500' :
                    module.priority === 'recommended' ? 'bg-blue-500' : 'bg-gray-400'
                  }`}></div>
                  <div>
                    <div className="font-medium capitalize">{module.name}</div>
                    <div className="text-sm text-gray-600">{module.reason}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <button onClick={handleContinue} className="btn-primary w-full py-3 text-lg">
            대시보드로 이동
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-accent-50 py-12">
      <div className="max-w-2xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="w-20 h-20 bg-gradient-to-br from-primary-500 to-accent-500 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
            <span className="text-white font-bold text-2xl">AI</span>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-3">
            All-in-One Business AI
          </h1>
          <p className="text-lg text-gray-600">
            비즈니스를 설명해주세요. AI가 최적의 워크스페이스를 만들어드립니다.
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="card mb-8">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            어떤 비즈니스를 운영하고 계신가요?
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="예: 강남에서 카페를 운영하고 있어요. 직원 2명과 함께 일하고 있고, 고객 관리와 재고 관리가 어려워요..."
            className="textarea h-40 mb-4"
            disabled={isLoading}
          />
          <p className="text-sm text-gray-500 mb-4">
            사업 종류, 규모, 어려운 점, 목표 등을 자유롭게 적어주세요.
          </p>
          <button
            type="submit"
            className="btn-primary w-full py-3"
            disabled={isLoading || !description.trim()}
          >
            워크스페이스 만들기
          </button>
        </form>

        {/* Examples */}
        <div>
          <h3 className="text-sm font-medium text-gray-500 mb-3">예시를 클릭해보세요</h3>
          <div className="space-y-2">
            {exampleDescriptions.map((example, idx) => (
              <button
                key={idx}
                onClick={() => setDescription(example)}
                className="w-full text-left p-4 bg-white rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition-colors text-sm text-gray-700"
                disabled={isLoading}
              >
                {example}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function EfficiencyCard({ metric, icon }: { metric: EfficiencyMetric; icon: string }) {
  const getScoreColor = (score: number) => {
    if (score >= 70) return 'text-green-600';
    if (score >= 50) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <div className="card-compact text-center">
      <div className="text-2xl mb-1">{icon}</div>
      <div className="text-xs text-gray-500 mb-1">{metric.label}</div>
      <div className="flex items-center justify-center gap-1">
        <span className={`text-lg font-bold ${getScoreColor(metric.currentScore)}`}>
          {metric.currentScore}
        </span>
        <span className="text-gray-400">→</span>
        <span className="text-lg font-bold text-green-600">
          {metric.potentialScore}
        </span>
      </div>
      <div className="text-xs text-green-600 font-medium mt-0.5">
        +{metric.improvement}% 개선
      </div>
    </div>
  );
}

function RecommendationItem({ recommendation }: { recommendation: ROIRecommendation }) {
  const impactColors = {
    high: 'bg-green-100 text-green-700',
    medium: 'bg-blue-100 text-blue-700',
    low: 'bg-gray-100 text-gray-700',
  };

  const categoryIcons = {
    time: '⏱️',
    cost: '💰',
    automation: '⚡',
    growth: '📈',
  };

  return (
    <div className="flex items-start gap-3 p-2 rounded-lg hover:bg-gray-50">
      <span className="text-lg">{categoryIcons[recommendation.category]}</span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-sm text-gray-900">{recommendation.title}</span>
          <span className={`text-xs px-1.5 py-0.5 rounded ${impactColors[recommendation.impact]}`}>
            {recommendation.impact === 'high' ? '높음' : recommendation.impact === 'medium' ? '중간' : '낮음'}
          </span>
        </div>
        <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{recommendation.description}</p>
        {(recommendation.estimatedSavings || recommendation.estimatedTimeHours) && (
          <div className="text-xs text-green-600 mt-0.5">
            {recommendation.estimatedSavings && `₩${recommendation.estimatedSavings.toLocaleString()} 절감`}
            {recommendation.estimatedSavings && recommendation.estimatedTimeHours && ' / '}
            {recommendation.estimatedTimeHours && `${recommendation.estimatedTimeHours}시간/월 절약`}
          </div>
        )}
      </div>
    </div>
  );
}
